import React, { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { Link } from 'react-router-dom';
import { Briefcase, TrendingUp, Shield, Heart, Star, Users, CheckCircle, ArrowRight, ChevronDown, ChevronUp, MapPin, Mail, FileText, Send, X } from 'lucide-react';
import CareerImg from '../assets/image/Career.jpg';

const openings = [
  { role: 'Sales & Marketing Executive', dept: 'Sales', type: 'Full-time', location: 'Tamil Nadu', desc: 'Drive pharmaceutical product sales across assigned territories, build relationships with healthcare professionals, and expand market presence.' },
  { role: 'Area Sales Manager', dept: 'Sales', type: 'Full-time', location: 'Multiple Locations', desc: 'Lead a team of sales executives, develop strategic sales plans, and achieve regional targets across the pharmaceutical portfolio.' },
  { role: 'Packaging & Quality Control', dept: 'Operations', type: 'Full-time', location: 'Tamil Nadu', desc: 'Ensure packaging meets quality standards, maintain records, and uphold compliance with pharmaceutical packaging regulations.' },
  { role: 'Logistics & Delivery Executive', dept: 'Logistics', type: 'Full-time', location: 'Tamil Nadu', desc: 'Manage timely and accurate delivery of pharmaceutical products, maintain cold-chain integrity, and handle inventory tracking.' },
  { role: 'Accounts & Administration', dept: 'Finance', type: 'Full-time', location: 'Tamil Nadu', desc: 'Handle billing, invoicing, credit management, GST filings, and financial reporting for the company operations.' },
  { role: 'Driver (Delivery Vehicle)', dept: 'Logistics', type: 'Full-time', location: 'Tamil Nadu', desc: 'Safe transportation of pharmaceutical products across designated routes with responsibility for vehicle maintenance and delivery documentation.' },
];

const perks = [
  { icon: TrendingUp, title: 'Growth & Development', desc: 'Opportunities to learn, upskill, and advance your career through hands-on experience and continuous improvement.' },
  { icon: Shield, title: 'Safe & Healthy Environment', desc: 'As a healthcare company, we maintain strict safety, hygiene, and quality standards across all operations.' },
  { icon: Heart, title: 'Respect & Inclusion', desc: 'We foster a culture where every individual is respected, heard, and valued regardless of their role.' },
  { icon: Star, title: 'Recognition & Rewards', desc: 'We appreciate hard work and reward performance with growth opportunities and incentives.' },
  { icon: Users, title: 'Work-Life Balance', desc: 'We support a balanced work culture to ensure both professional success and personal well-being.' },
  { icon: Briefcase, title: 'Pharma Exposure', desc: 'Gain valuable experience in the growing pharmaceutical industry with exposure to industry standards.' },
];

const deptColors = { Sales: 'bg-teal-100 text-teal-700', Operations: 'bg-blue-100 text-blue-700', Logistics: 'bg-orange-100 text-orange-700', Finance: 'bg-purple-100 text-purple-700' };

const CAREERS_EMAIL = 'info@bleen.in';

const emptyApplication = {
  firstName: '',
  email: '',
  phone: '',
  experience: '',
  resumeName: '',
};

const experienceOptions = ['0-1 Years', '1-3 Years', '3-5 Years', '5-8 Years', '8+ Years'];

function buildApplyMailto(job, form) {
  const subject = `Job Application - ${job.role}`;
  const body =
    `Dear Hiring Team,\n\n` +
    `I am writing to apply for the position of ${job.role} (${job.dept} department, ${job.location}) at Galaxy Health Care.\n\n` +
    `My Details:\n` +
    `First Name: ${form.firstName}\n` +
    `Phone Number: ${form.phone}\n` +
    `Email Address: ${form.email}\n` +
    `Years of Experience: ${form.experience}\n` +
    `Resume File: ${form.resumeName || 'Will attach separately'}\n\n` +
    `Please find my resume attached for your consideration.\n\n` +
    `Thank you for your time and consideration. I look forward to hearing from you.\n\n` +
    `Best regards,\n${form.firstName}`;

  return `mailto:${CAREERS_EMAIL}?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`;
}

function ApplyPopup({ job, onClose, onSubmitted }) {
  const [form, setForm] = useState(emptyApplication);

  useEffect(() => {
    const original = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    const onKey = (event) => event.key === 'Escape' && onClose();
    window.addEventListener('keydown', onKey);
    return () => {
      document.body.style.overflow = original;
      window.removeEventListener('keydown', onKey);
    };
  }, [onClose]);

  if (!job) return null;

  const updateField = (field, value) => setForm((current) => ({ ...current, [field]: value }));

  const submit = (event) => {
    event.preventDefault();
    window.location.href = buildApplyMailto(job, form);
    onSubmitted();
  };

  return createPortal(
    <div
      className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/50 p-4 backdrop-blur-sm"
      onClick={onClose}
      role="dialog"
      aria-modal="true"
    >
      <div
        className="w-full max-w-4xl overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-2xl"
        onClick={(event) => event.stopPropagation()}
      >
        <div className="h-1.5 w-full bg-gradient-to-r from-teal-400 to-teal-600" />
        <div className="flex items-start justify-between gap-4 border-b border-slate-100 px-6 py-5">
          <div>
            <div className="text-xs font-black uppercase tracking-widest text-teal-600">Apply Now</div>
            <h3 className="mt-1 text-2xl font-black leading-tight text-slate-900">{job.role}</h3>
            <p className="mt-1 text-sm text-slate-500">{job.dept} / {job.location}</p>
          </div>
          <button
            type="button"
            onClick={onClose}
            aria-label="Close"
            className="rounded-full border border-slate-200 p-2 text-slate-500 transition-colors hover:border-slate-300 hover:text-slate-900"
          >
            <X size={16} />
          </button>
        </div>

        <form onSubmit={submit} className="px-6 py-7">
          <div className="grid gap-x-6 gap-y-5 md:grid-cols-2">
            <label className="block">
              <span className="sr-only">First Name</span>
              <input
                type="text"
                required
                value={form.firstName}
                onChange={(event) => updateField('firstName', event.target.value)}
                placeholder="First Name"
                className="w-full border-0 border-b border-slate-300 bg-transparent px-0 py-3 text-sm font-semibold text-slate-900 outline-none transition-colors placeholder:text-slate-500 focus:border-teal-500"
              />
            </label>
            <label className="block">
              <span className="sr-only">Email</span>
              <input
                type="email"
                required
                value={form.email}
                onChange={(event) => updateField('email', event.target.value)}
                placeholder="Email"
                className="w-full border-0 border-b border-slate-300 bg-transparent px-0 py-3 text-sm font-semibold text-slate-900 outline-none transition-colors placeholder:text-slate-500 focus:border-teal-500"
              />
            </label>
            <label className="block">
              <span className="sr-only">Phone Number</span>
              <input
                type="tel"
                required
                value={form.phone}
                onChange={(event) => updateField('phone', event.target.value)}
                placeholder="Phone Number"
                className="w-full border-0 border-b border-slate-300 bg-transparent px-0 py-3 text-sm font-semibold text-slate-900 outline-none transition-colors placeholder:text-slate-500 focus:border-teal-500"
              />
            </label>
            <label className="block">
              <span className="sr-only">Years of Experience</span>
              <select
                required
                value={form.experience}
                onChange={(event) => updateField('experience', event.target.value)}
                className="w-full border-0 border-b border-slate-300 bg-transparent px-0 py-3 text-sm font-semibold text-slate-900 outline-none transition-colors focus:border-teal-500"
              >
                <option value="">Years of Experience</option>
                {experienceOptions.map((option) => (
                  <option key={option} value={option}>{option}</option>
                ))}
              </select>
            </label>
          </div>

          <div className="mt-7 flex flex-col gap-5 md:flex-row md:items-center md:justify-between">
            <label className="inline-flex w-fit cursor-pointer items-center gap-2 rounded-xl border border-teal-200 bg-teal-50 px-6 py-3 text-sm font-bold text-teal-700 transition-all hover:-translate-y-0.5 hover:border-teal-300 hover:bg-teal-100 hover:shadow-md">
              <FileText size={16} />
              <span>{form.resumeName || 'Upload Your Resume'}</span>
              <input
                type="file"
                accept=".pdf,.doc,.docx"
                className="sr-only"
                onChange={(event) => updateField('resumeName', event.target.files?.[0]?.name || '')}
              />
            </label>
            <button
              type="submit"
              className="btn-shimmer inline-flex w-fit items-center gap-2 self-center rounded-xl px-8 py-3 text-sm font-black text-white shadow-lg"
            >
              <span className="grid h-5 w-5 place-items-center rounded-full bg-white/20 text-white">
                <Send size={12} />
              </span>
              Apply
            </button>
          </div>
          <p className="mt-5 text-xs leading-relaxed text-slate-400">
            Your email app will open with these details filled in. Please attach the selected resume before sending.
          </p>
        </form>
      </div>
    </div>,
    document.body
  );
}

export default function Careers() {
  const [expanded, setExpanded] = useState(null);
  const [applied, setApplied] = useState(null);
  const [activeJob, setActiveJob] = useState(null);

  return (
    <div>
      {/* Hero */}
      <img src={CareerImg} alt="Careers" className="w-full h-auto block" />

      {/* Why work with us */}
      <section className="py-20 px-4 bg-white">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-14 reveal">
            <div className="gradient-text text-sm font-bold uppercase tracking-widest mb-3">Our Culture</div>
            <h2 className="text-4xl font-black text-gray-900 mb-4">Why Work With Us?</h2>
            <p className="text-gray-500 max-w-xl mx-auto">We are not just a company — we are a growing healthcare brand focused on innovation, quality, and trust.</p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {perks.map(({ icon: Icon, title, desc }, i) => (
              <div key={title} className={`reveal delay-${(i % 3 + 1) * 100} card-hover p-6 rounded-2xl border border-gray-100 hover:border-teal-200 bg-gradient-to-br from-white to-gray-50 group`}>
                <div className="w-12 h-12 gradient-bg rounded-xl flex items-center justify-center mb-4 shadow-md group-hover:scale-110 transition-transform">
                  <Icon size={22} className="text-white" />
                </div>
                <h3 className="font-bold text-gray-900 mb-2">{title}</h3>
                <p className="text-gray-500 text-sm leading-relaxed">{desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Work culture */}
      <section className="py-16 px-4 bg-gray-50">
        <div className="max-w-5xl mx-auto">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div className="reveal">
              <div className="gradient-text text-sm font-bold uppercase tracking-widest mb-3">What We Believe</div>
              <h2 className="text-4xl font-black text-gray-900 mb-6">Our Work Culture</h2>
              <div className="space-y-4">
                {[
                  'Hiring the right people for the right roles',
                  'Encouraging teamwork and responsibility',
                  'Maintaining high-quality standards in every process',
                  'Supporting continuous learning and improvement',
                  'Growing stronger with skilled professionals committed to excellence',
                ].map(item => (
                  <div key={item} className="flex items-center gap-3">
                    <CheckCircle size={18} className="text-teal-500 flex-shrink-0" />
                    <span className="text-gray-700 font-medium">{item}</span>
                  </div>
                ))}
              </div>
            </div>
            <div className="reveal delay-200">
              <div className="gradient-bg rounded-3xl p-8 text-white shadow-2xl">
                <h3 className="text-2xl font-black mb-4">Who Can Apply?</h3>
                <p className="text-white/80 mb-4">We welcome candidates who are:</p>
                <div className="space-y-3">
                  {['Passionate about the pharma/healthcare industry', 'Detail-oriented and responsible', 'Willing to learn and grow', 'Team players with a positive attitude', 'Committed to quality and excellence'].map(item => (
                    <div key={item} className="flex items-center gap-3 bg-white/10 rounded-xl p-3">
                      <Star size={14} className="text-white/80 flex-shrink-0" />
                      <span className="text-sm font-medium">{item}</span>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Open positions */}
      <section className="py-20 px-4 bg-white">
        <div className="max-w-5xl mx-auto">
          <div className="text-center mb-14 reveal">
            <div className="gradient-text text-sm font-bold uppercase tracking-widest mb-3">Opportunities</div>
            <h2 className="text-4xl font-black text-gray-900 mb-4">Current Openings</h2>
            <p className="text-gray-500">We are actively looking for talented individuals across these roles.</p>
          </div>
          <div className="space-y-4">
            {openings.map((job, i) => (
              <div key={job.role} className={`reveal delay-${(i % 3 + 1) * 100} bg-white border border-gray-100 hover:border-teal-200 rounded-2xl shadow-sm hover:shadow-md transition-all overflow-hidden`}>
                <button
                  onClick={() => setExpanded(expanded === i ? null : i)}
                  className="w-full flex items-center justify-between p-5 text-left"
                >
                  <div className="flex items-center gap-4">
                    <div className="w-12 h-12 gradient-bg rounded-xl flex items-center justify-center flex-shrink-0 shadow-md">
                      <Briefcase size={20} className="text-white" />
                    </div>
                    <div>
                      <h3 className="font-bold text-gray-900 text-base">{job.role}</h3>
                      <div className="flex items-center gap-3 mt-1">
                        <span className={`text-xs font-semibold px-2.5 py-0.5 rounded-full ${deptColors[job.dept]}`}>{job.dept}</span>
                        <span className="text-xs text-gray-500">{job.type}</span>
                        <span className="text-xs text-gray-400 inline-flex items-center gap-1">
                          <MapPin size={11} /> {job.location}
                        </span>
                      </div>
                    </div>
                  </div>
                  {expanded === i ? <ChevronUp size={18} className="text-teal-600 flex-shrink-0" /> : <ChevronDown size={18} className="text-gray-400 flex-shrink-0" />}
                </button>
                {expanded === i && (
                  <div className="px-5 pb-5 border-t border-gray-50 pt-4">
                    <p className="text-gray-600 text-sm mb-4 leading-relaxed">{job.desc}</p>
                    {applied === i ? (
                      <div className="bg-teal-50 border border-teal-200 rounded-xl p-4 flex items-center gap-3 text-teal-700">
                        <CheckCircle size={18} />
                        <span className="font-semibold text-sm">Your email app should be open with the application ready to send. If nothing opened, email us directly at {CAREERS_EMAIL}.</span>
                      </div>
                    ) : (
                      <div className="flex gap-3">
                        <button
                          type="button"
                          onClick={() => setActiveJob({ ...job, index: i })}
                          className="inline-flex items-center gap-2 rounded-full bg-teal-600 px-6 py-2.5 text-sm font-bold text-white shadow-md transition-all hover:-translate-y-0.5 hover:bg-teal-700 hover:shadow-lg">
                          Apply Now <ArrowRight size={16} />
                        </button>
                      </div>
                    )}
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-16 px-4 bg-gradient-to-br from-gray-900 to-gray-950 text-white">
        <div className="max-w-3xl mx-auto text-center reveal">
          <div className="text-teal-400 text-sm font-bold uppercase tracking-widest mb-3">Don't See Your Role?</div>
          <h2 className="text-4xl font-black mb-4">Send Us Your CV</h2>
          <p className="text-gray-400 mb-8">We are always looking for talented individuals passionate about healthcare. Send your resume and we'll keep it in consideration for future openings.</p>
          <a
            href={`mailto:${CAREERS_EMAIL}?subject=${encodeURIComponent('Job Application - General / Future Opportunities')}&body=${encodeURIComponent(
              'Dear Hiring Team,\n\nI am interested in future opportunities at Galaxy Health Care and would like to submit my resume/CV for consideration.\n\nMy Details:\nName: \nPhone Number: \nEmail Address: \nCurrent Location: \nArea of Interest: \nTotal Experience: \n\nThank you for your time and consideration.\n\nBest regards,\n'
            )}`}
            className="btn-shimmer text-white px-10 py-4 rounded-xl font-bold inline-flex items-center gap-2 shadow-lg text-base"
          >
            <Mail size={20} /> Contact Us <ArrowRight size={20} />
          </a>
        </div>
      </section>

      {activeJob && (
        <ApplyPopup
          job={activeJob}
          onClose={() => setActiveJob(null)}
          onSubmitted={() => {
            setApplied(activeJob.index);
            setActiveJob(null);
          }}
        />
      )}
    </div>
  );
}
